// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { ViewContext } from '../viewContext';
import type { Sex } from '../types';
import { SEX_COLOUR, STATE_COLOUR } from '../colours';
import { escapeHtml } from '../utils/format';
import { rankListHtml, segHtml, topNAt, viewHead, wireNameRows } from './shared';

export function renderStates(root: HTMLElement, ctx: ViewContext): void {
  const { names, meta } = ctx.data;
  let sex: Sex = 'girl';
  let year = meta.latestYear;

  // both states overlap from 1960; offer 5-year steps plus the latest year
  const yearOptions = [...Array(meta.latestYear - 1960 + 1)].map((_, i) => 1960 + i).filter((y) => y % 5 === 0 || y === meta.latestYear);

  root.innerHTML = `
    ${viewHead(
      'New South Wales vs Queensland',
      'Do the two states name their babies differently? Compare the top lists for the same year, and see which names are markedly more popular in one state than the other.',
    )}
    <div class="panel">
      <div class="controls">
        ${segHtml('Sex', [
          { id: 'girl', label: 'Girls' },
          { id: 'boy', label: 'Boys' },
        ], sex)}
        <span class="field-label" style="margin-left:var(--space-md)">Year:</span>
        <select data-role="year" aria-label="Year">
          ${yearOptions.reverse().map((y) => `<option value="${y}" ${y === year ? 'selected' : ''}>${y}</option>`).join('')}
        </select>
      </div>
    </div>

    <div class="grid-2">
      <div class="panel">
        <div class="panel-title"><span class="pill" style="background:${STATE_COLOUR.NSW}">NSW</span> Top 12</div>
        <div class="panel-sub" data-role="nsw-sub"></div>
        <div data-role="nsw"></div>
      </div>
      <div class="panel">
        <div class="panel-title"><span class="pill" style="background:${STATE_COLOUR.QLD}">QLD</span> Top 12</div>
        <div class="panel-sub" data-role="qld-sub"></div>
        <div data-role="qld"></div>
      </div>
    </div>

    <div class="grid-2">
      <div class="panel">
        <div class="panel-title">More popular in NSW</div>
        <div class="panel-sub">Names in both states' top 100 this year that rank markedly higher in New South Wales.</div>
        <div data-role="skew-nsw"></div>
      </div>
      <div class="panel">
        <div class="panel-title">More popular in QLD</div>
        <div class="panel-sub">…and the names that rank markedly higher in Queensland.</div>
        <div data-role="skew-qld"></div>
      </div>
    </div>
  `;

  const yearSel = root.querySelector('[data-role="year"]') as HTMLSelectElement;

  function skewRow(name: string, key: string, nswR: number, qldR: number): string {
    const diff = qldR - nswR;
    const better = diff > 0 ? 'NSW' : 'QLD';
    const tip = escapeHtml(`${name}: #${nswR} in NSW, #${qldR} in QLD`);
    return `<div class="rank-row" data-key="${escapeHtml(key)}" role="button" tabindex="0" data-tip="${tip}">
      <span class="rank-name">${escapeHtml(name)}</span>
      <span class="rank-count" style="width:auto;color:var(--text-tertiary)">NSW #${nswR} · QLD #${qldR}</span>
      <span class="mover-delta ${better === 'NSW' ? 'up' : 'down'}" style="width:52px">${Math.abs(diff)}</span>
    </div>`;
  }

  function paint(): void {
    const nswTop = topNAt(names, `NSW-${sex}`, year, 12);
    const qldTop = topNAt(names, `QLD-${sex}`, year, 12);
    const colour = SEX_COLOUR[sex];
    (root.querySelector('[data-role="nsw"]') as HTMLElement).innerHTML = nswTop.length
      ? rankListHtml(nswTop, colour)
      : '<div class="empty">No data for this year.</div>';
    (root.querySelector('[data-role="qld"]') as HTMLElement).innerHTML = qldTop.length
      ? rankListHtml(qldTop, colour)
      : '<div class="empty">No data for this year.</div>';
    (root.querySelector('[data-role="nsw-sub"]') as HTMLElement).textContent = `${sex === 'boy' ? 'Boys' : 'Girls'}, ${year}`;
    (root.querySelector('[data-role="qld-sub"]') as HTMLElement).textContent = `${sex === 'boy' ? 'Boys' : 'Girls'}, ${year}`;

    // skew: names in both states' top-100
    const nsw100 = new Map(topNAt(names, `NSW-${sex}`, year, 100).map((r) => [r.name.k, { name: r.name.n, rank: r.rank }]));
    const qld100 = new Map(topNAt(names, `QLD-${sex}`, year, 100).map((r) => [r.name.k, r.rank]));
    const both: Array<{ name: string; key: string; nsw: number; qld: number; diff: number }> = [];
    for (const [k, v] of nsw100) {
      const q = qld100.get(k);
      if (q != null) both.push({ name: v.name, key: k, nsw: v.rank, qld: q, diff: q - v.rank });
    }
    const nswSkew = both.filter((b) => b.diff > 0).sort((a, b) => b.diff - a.diff).slice(0, 10);
    const qldSkew = both.filter((b) => b.diff < 0).sort((a, b) => a.diff - b.diff).slice(0, 10);
    (root.querySelector('[data-role="skew-nsw"]') as HTMLElement).innerHTML = nswSkew.length
      ? `<div class="rank-list">${nswSkew.map((b) => skewRow(b.name, b.key, b.nsw, b.qld)).join('')}</div>`
      : '<div class="empty">No shared names this year.</div>';
    (root.querySelector('[data-role="skew-qld"]') as HTMLElement).innerHTML = qldSkew.length
      ? `<div class="rank-list">${qldSkew.map((b) => skewRow(b.name, b.key, b.nsw, b.qld)).join('')}</div>`
      : '<div class="empty">No shared names this year.</div>';
  }

  root.querySelectorAll('[data-opt]').forEach((b) =>
    b.addEventListener('click', () => {
      root.querySelectorAll('[data-opt]').forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
      sex = b.getAttribute('data-opt') as Sex;
      paint();
    }),
  );
  yearSel.addEventListener('change', () => {
    year = Number(yearSel.value);
    paint();
  });

  wireNameRows(root, ctx.openName);
  paint();
}
